// A találkozók MUNKAIDŐ-ABLAKA és az előtöltött időpontok — egy helyen, tiszta függvényekben.
//
// MIÉRT ITT: ugyanez a három szám (nyitás, zárás, alap-kezdés) HÁROM helyen kell — a naptár rács-sávjai
// (`slotMinTime`/`slotMaxTime`), a szerkesztő-form előtöltése és a mentés-előtti validáció. Ha bármelyik
// helyen külön szám állna, a felület olyan időpontot ajánlana fel, amit a validáció visszadob (vagy amit a
// naptár nem is jelenít meg).
//
// A számokat a VALÓS adat is alátámasztja: az 1360 migrált legacy találkozó közül EGY sem esik 06:00 elé vagy
// 22:00 utánra (mérve az Odin dev-adatbázisán), tehát az ablak nem szűkíti a meglévő gyakorlatot,
// csak kimondja.
//
// A számítás a MEGJELENÍTÉSI zóna FAL-IDEJÉN megy ('YYYY-MM-DDTHH:mm' stringek) — nincs `Date`, nincs zóna-
// konverzió, tehát DST-váltás sem tudja elcsúsztatni (a form/naptár mindenhol ezt az alakot használja).

import { atDefaultStartHour, DEFAULT_START_HOUR, nextDefaultStart } from '@/lib/workday';

/** A nap első választható órája (a naptár `slotMinTime`-ja is ez). */
export const BUSINESS_START_HOUR = 6;
/** A nap utolsó megengedett VÉG-órája (a naptár `slotMaxTime`-ja is ez). */
export const BUSINESS_END_HOUR = 22;
/**
 * Az alapértelmezett kezdés, ha a felhasználó csak NAPOT választott (nem idősávot). A SZÁM a közös
 * `lib/workday.ts`-ből jön (2. fogyasztó: az üzenet-időzítés) — itt csak re-exportáljuk, hogy a modul
 * meglévő importjai (és a naptár-rács kódja) változatlanok maradjanak.
 */
export { DEFAULT_START_HOUR } from '@/lib/workday';
/** Az alapértelmezett hossz órában (a legacy form is 1 órát ajánlott). */
export const DEFAULT_DURATION_HOURS = 1;

/** 'YYYY-MM-DDTHH:mm' → { date, minutes (éjfél óta) }; érvénytelen alak → null. */
function parseLocalInput(local: string): { date: string; minutes: number } | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!m) return null;
  const h = Number(m[2]);
  const min = Number(m[3]);
  if (h > 23 || min > 59) return null;
  // A regex illeszkedése garantálja a 3 csoportot — a `!` itt a szabályos alak (a lint is ezt kéri).
  return { date: m[1]!, minutes: h * 60 + min };
}

/** { date, minutes } → 'YYYY-MM-DDTHH:mm' (a perc a NAPON belül marad — a hívó gondoskodik a szorításról). */
function toLocalInput(date: string, minutes: number): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date}T${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}

/**
 * A kattintásból származó kezdő-időpont a form számára.
 *  • NAP-kattintás (hónap-nézet, `allDay`) → a nap `DEFAULT_START_HOUR`-ja: éjfélre nem rögzítünk találkozót,
 *    a lib pedig a nap-cellára 00:00-t ad.
 *  • IDŐSÁV-kattintás (hét/nap nézet) → a kattintott idő, a munkaidő-ablakba szorítva (a rács ugyan csak
 *    06:00–22:00-t mutat, de a szorítás védi a jövőbeli rács-változást is).
 * Érvénytelen bemenet → üres string (a form üresen indul, a `min(1)` validáció szól).
 */
export function slotStartToFormStart(local: string, allDay: boolean): string {
  const p = parseLocalInput(local);
  if (!p) return '';
  if (allDay) return toLocalInput(p.date, DEFAULT_START_HOUR * 60);
  const lastStart = BUSINESS_END_HOUR * 60 - DEFAULT_DURATION_HOURS * 60;
  const clamped = Math.min(Math.max(p.minutes, BUSINESS_START_HOUR * 60), lastStart);
  return toLocalInput(p.date, clamped);
}

/**
 * A kezdéshez tartozó alapértelmezett VÉG: +`DEFAULT_DURATION_HOURS`, de legfeljebb a záró óráig (a NAPON
 * belül marad — egy 21:30-as kezdés 22:00-kor végződik, nem másnap 22:30-kor).
 */
export function defaultEndFor(startLocal: string): string {
  const p = parseLocalInput(startLocal);
  if (!p) return '';
  const end = Math.min(p.minutes + DEFAULT_DURATION_HOURS * 60, BUSINESS_END_HOUR * 60);
  return toLocalInput(p.date, end);
}

/**
 * Az „Új megbeszélés" gomb (naptár-sáv nélküli) alap-kezdése a MOSTHOZ igazítva (a Google Naptár mintája):
 * reggel a munkanap-kezdés (08:00) előtt a mai 08:00 · napközben a KÖVETKEZŐ EGÉSZ ÓRA, ha az alap-hosszú (1 órás)
 * megbeszélés még a záró óra (22:00) előtt véget ér · különben holnap 08:00. MIÉRT: a vak „ma 08:00" este (és
 * délután is) múltbeli időpontot ajánlott (kolléga-észrevétel). Tiszta függvény: a hívó a megjelenítési zóna mai
 * dátumát és a mostani perceit adja.
 */
export function defaultStartForNow(todayInDisplayZone: string, nowMinutes: number): string {
  if (nowMinutes < DEFAULT_START_HOUR * 60) return atDefaultStartHour(todayInDisplayZone);
  const nextHour = Math.floor(nowMinutes / 60) + 1;
  if ((nextHour + DEFAULT_DURATION_HOURS) * 60 <= BUSINESS_END_HOUR * 60) {
    return toLocalInput(todayInDisplayZone, nextHour * 60);
  }
  return nextDefaultStart(todayInDisplayZone, nowMinutes);
}

/**
 * Munkaidő-ablakon belül van-e az időpont? A KEZDÉS-re és a VÉG-re is ezt hívjuk: a kezdés nem lehet 06:00
 * előtt, a vég nem lehet 22:00 után (a pontosan 22:00-as vég MEGENGEDETT — az a záró pillanat).
 */
export function isWithinBusinessHours(local: string): boolean {
  const p = parseLocalInput(local);
  if (!p) return false;
  return p.minutes >= BUSINESS_START_HOUR * 60 && p.minutes <= BUSINESS_END_HOUR * 60;
}
